import React from 'react';
import { Box, Button, Container, Paper, Stack, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  useWebauthnAuthOptionsMutation,
  useWebauthnAuthVerifyMutation,
  useWebauthnRegisterOptionsMutation,
  useWebauthnRegisterVerifyMutation,
} from '@/services/authApi';
import { credToJSON, toPublicKeyCredentialCreationOptions, toPublicKeyCredentialRequestOptions } from '@/utils/webauthn';
import { useDispatch } from 'react-redux';
import { setUser } from '@/store/userSlice';

export default function MFASetupPrompt() {
  const navigate = useNavigate();
  const location = useLocation() as any;
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const from = (location.state?.from as string) || '/dashboard';
  const preAuthToken = (location.state?.preAuthToken as string) || undefined;
  const availableMfaMethods = (location.state?.availableMfaMethods as Array<'totp' | 'passkey'>) || [];
  const enrollableMfaMethods = (location.state?.enrollableMfaMethods as Array<'totp' | 'passkey'>) || [];
  const preferredMfaMethod = (location.state?.preferredMfaMethod as 'totp' | 'passkey' | null | undefined) || null;
  const PASSKEYS_ENABLED = String(import.meta.env.VITE_PASSKEYS_ENABLED || 'true') !== 'false';
  const isPasskeySupported = PASSKEYS_ENABLED && typeof window !== 'undefined' && 'PublicKeyCredential' in window;
  const [webauthnAuthOptions] = useWebauthnAuthOptionsMutation();
  const [webauthnAuthVerify] = useWebauthnAuthVerifyMutation();
  const [webauthnRegisterOptions] = useWebauthnRegisterOptionsMutation();
  const [webauthnRegisterVerify] = useWebauthnRegisterVerifyMutation();

  const completeLogin = (response: any) => {
    const userData = {
      userId: response.userId || null,
      name: response.name || null,
      email: response.email || null,
      role: response.role || null,
      jwtToken: response.jwtToken || null,
      amcId: (response as any).amcId ?? null,
    };
    dispatch(setUser(userData));
    navigate(from, { replace: true });
  };

  const handlePasskeyAuth = async () => {
    try {
      if (!isPasskeySupported) throw new Error('Passkey not supported');
      const optionsJson = await webauthnAuthOptions(preAuthToken ? { preAuthToken } : {}).unwrap();
      const requestOptions = toPublicKeyCredentialRequestOptions(optionsJson);
      const assertion: any = await navigator.credentials.get({ publicKey: requestOptions });
      const serialized = credToJSON(assertion);
      const res = await webauthnAuthVerify({ preAuthToken, assertionResponse: serialized, rememberMethod: true }).unwrap();
      completeLogin(res);
    } catch (err: any) {
      enqueueSnackbar('Passkey authentication not available. You can register a passkey or use TOTP.', { variant: 'info' });
    }
  };

  const handlePasskeyRegister = async () => {
    try {
      if (!isPasskeySupported) throw new Error('Passkey not supported');
      const creationOptionsJson = await webauthnRegisterOptions(preAuthToken ? { preAuthToken } : {}).unwrap();
      const creationOptions = toPublicKeyCredentialCreationOptions(creationOptionsJson);
      const cred: any = await navigator.credentials.create({ publicKey: creationOptions });
      const serialized = credToJSON(cred);
      const res = await webauthnRegisterVerify({ preAuthToken, attestationResponse: serialized, rememberMethod: true }).unwrap();
      completeLogin(res);
    } catch (err: any) {
      enqueueSnackbar('Passkey registration failed or was cancelled. You can try TOTP instead.', { variant: 'warning' });
    }
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Stack spacing={2}>
            <Typography component="h1" variant="h5" gutterBottom>
              Protect your account with 2FA
            </Typography>
            <Typography variant="body2">
              We recommend enabling two-factor authentication for enhanced security.
            </Typography>

            {(availableMfaMethods.length > 0) && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Use a method now</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {availableMfaMethods.includes('totp') && (
                    <Button
                      variant="outlined"
                      onClick={() => navigate('/login/2fa', { state: { from, preAuthToken, availableMfaMethods, preferredMfaMethod }, replace: true })}
                    >
                      Verify with TOTP
                    </Button>
                  )}
                  {availableMfaMethods.includes('passkey') && isPasskeySupported && (
                    <Button variant="outlined" onClick={handlePasskeyAuth}>Use Passkey</Button>
                  )}
                </Stack>
              </Box>
            )}

            {(enrollableMfaMethods.length > 0) && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Set up a method</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {enrollableMfaMethods.includes('totp') && (
                    <Button variant="contained" onClick={() => navigate('/mfa/setup', { state: { from, preAuthToken }, replace: true })}>
                      Set up TOTP
                    </Button>
                  )}
                  {enrollableMfaMethods.includes('passkey') && isPasskeySupported && (
                    <Button variant="contained" onClick={handlePasskeyRegister}>Register a Passkey</Button>
                  )}
                </Stack>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button variant="text" onClick={() => navigate(from, { replace: true })}>
                Skip for now
              </Button>
            </Box>
          </Stack>
        </Paper>
      </Box>
    </Container>
  );
}
